import React from 'react';
import { Authenticator, ThemeProvider, useTheme, useAuthenticator, View, Image, Text, Heading } from '@aws-amplify/ui-react';

/**
 * Branded shell around the Cognito sign-in form.
 *
 * Visuals:
 *  - Full-bleed dark navy page with a subtle cyan/magenta radial gradient
 *    (matches the existing app palette in App.css).
 *  - Centered card with the Pulse Storage logo, tagline, and the Authenticator.
 *  - Brand-colored primary button + focus rings via Amplify's theme tokens.
 *
 * The logo file is the one already shipped in /public — keeps this component
 * dependency-free.
 */

const Header = () => {
  const { tokens } = useTheme();
  return (
    <View textAlign="center" padding={tokens.space.large}>
      <Image
        alt="Pulse Storage"
        src="/logo-full.png"
        height="72px"
        objectFit="contain"
        style={{ marginBottom: 8, filter: 'drop-shadow(0 0 18px rgba(224,64,251,0.25))' }}
      />
      <Text
        variation="tertiary"
        fontSize="0.85rem"
        color="#9aa3b2"
        letterSpacing="0.08em"
        style={{ textTransform: 'uppercase' }}
      >
        Call Recording Archive
      </Text>
    </View>
  );
};

const Footer = () => (
  <View textAlign="center" padding="0.75rem 1rem 1.5rem">
    <Text fontSize="0.75rem" color="#6b7280">
      HIPAA-compliant Medicare call retention · {new Date().getFullYear()}
    </Text>
  </View>
);

const SignInHeader = () => {
  const { tokens } = useTheme();
  return (
    <Heading
      level={5}
      padding={`${tokens.space.medium} ${tokens.space.medium} 0`}
      textAlign="center"
      color="#1a1a2e"
      fontWeight={600}
    >
      Sign in to your account
    </Heading>
  );
};

// Amplify UI theme — maps the existing brand palette (App.css :root vars) onto
// Amplify's design tokens so the form's button + focus ring + links all pick
// up the cyan accent we use elsewhere.
const loginTheme = {
  name: 'pulse-storage',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: { value: '#e0f7fa' },
          20: { value: '#b2ebf2' },
          40: { value: '#4dd0e1' },
          60: { value: '#00bcd4' },
          80: { value: '#0097a7' },
          90: { value: '#006064' },
          100: { value: '#004d50' },
        },
      },
      font: {
        interactive: { value: '#00838f' },
        focus: { value: '#00bcd4' },
      },
    },
    radii: {
      small: { value: '6px' },
      medium: { value: '10px' },
      large: { value: '14px' },
    },
    components: {
      authenticator: {
        router: {
          backgroundColor: { value: '#ffffff' },
          borderColor: { value: 'rgba(0, 188, 212, 0.18)' },
          boxShadow: { value: '0 24px 60px rgba(15, 15, 26, 0.55), 0 0 0 1px rgba(224, 64, 251, 0.15)' },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: '#00bcd4' },
          _hover: { backgroundColor: { value: '#00acc1' } },
          _focus: { backgroundColor: { value: '#00acc1' } },
          _active: { backgroundColor: { value: '#0097a7' } },
        },
      },
      fieldcontrol: {
        _focus: {
          borderColor: { value: '#00bcd4' },
          boxShadow: { value: '0 0 0 2px rgba(0, 188, 212, 0.18)' },
        },
      },
    },
  },
};

interface LoginShellProps {
  children: (props: { signOut?: () => void; user?: any }) => React.ReactElement;
}

// Splits unauthenticated vs authenticated rendering so the dark gradient +
// 420px-wide login chrome does NOT wrap the full app shell after sign-in.
function ShellInner({ children }: LoginShellProps) {
  const { authStatus, signOut, user } = useAuthenticator((c) => [c.authStatus, c.user]);

  if (authStatus === 'authenticated') {
    return children({ signOut, user });
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        background:
          'radial-gradient(circle at 18% 20%, rgba(0, 188, 212, 0.18), transparent 55%),' +
          'radial-gradient(circle at 82% 80%, rgba(224, 64, 251, 0.18), transparent 55%),' +
          'linear-gradient(135deg, #0f0f1a 0%, #161624 100%)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <Authenticator
          hideSignUp={true}
          components={{
            Header,
            Footer,
            SignIn: { Header: SignInHeader },
          }}
        />
      </div>
    </div>
  );
}

export default function LoginShell({ children }: LoginShellProps) {
  return (
    <ThemeProvider theme={loginTheme as any}>
      <Authenticator.Provider>
        <ShellInner>{children}</ShellInner>
      </Authenticator.Provider>
    </ThemeProvider>
  );
}
